import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { API_URL, APP_ENV } from 'src/global/config/common.config';
import { addBreadcrumb, captureException } from 'src/utils/sentry';

const logOnDev = (message: string, log?: AxiosResponse | InternalAxiosRequestConfig | AxiosError) => {
  if (APP_ENV === 'development') {
    console.log(message, log);
  }
};

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(async request => {
  const { method, url } = request;
  logOnDev(`🚀 [${method?.toUpperCase()}] ${url} | Request`, request);

  addBreadcrumb(`API Request: ${method?.toUpperCase()} ${url}`, 'http', 'info', {
    method: method?.toUpperCase(),
    url,
    baseURL: request.baseURL,
  });

  return request;
});

apiClient.interceptors.response.use(
  response => {
    const { method, url } = response.config;
    const { status } = response;

    logOnDev(`✨ [${method?.toUpperCase()}] ${url} | Response ${status}`, response);

    addBreadcrumb(`API Response: ${method?.toUpperCase()} ${url} - ${status}`, 'http', 'info', {
      method: method?.toUpperCase(),
      url,
      status,
      statusText: response.statusText,
    });

    return response;
  },
  async (error: AxiosError) => {
    const { message } = error;
    const { status, data } = error.response || {};
    const { method, url } = error.config || {};

    const errorMessage = `🚨 [${method?.toUpperCase()}] ${url} | Error ${status} ${(data as any)?.message || ''} | ${message}`;
    logOnDev(errorMessage, error);

    addBreadcrumb(`API Error: ${method?.toUpperCase()} ${url} - ${status}`, 'http', 'error', {
      method: method?.toUpperCase(),
      url,
      status,
      statusText: error.response?.statusText,
      errorMessage: (data as any)?.message || message,
    });

    captureException(error, {
      api: {
        method: method?.toUpperCase(),
        url,
        baseURL: error.config?.baseURL,
        status,
        statusText: error.response?.statusText,
        responseData: data,
      },
      request: {
        headers: error.config?.headers,
        params: error.config?.params,
        data: error.config?.data,
      },
    });

    return Promise.reject(error);
  },
);

export default apiClient;
